/**
 * Platformer autotiling — 16-variant "match sides" terrain (the same
 * model Tiled and Godot call a 4-bit / 16-tile terrain set).
 *
 * A tile def opts in via `autotile: { group, slot }` (see
 * `PLATFORMER_TILE_DEFS`). At paint time the renderer looks at the
 * four cardinal neighbours ON THE SAME LAYER and builds a bitmask of
 * which sides touch another tile of the same group:
 *
 *   bit 1 = North   bit 2 = East   bit 4 = South   bit 8 = West
 *
 * The mask (0–15) indexes straight into a 4×4 template image imported
 * for the group's `slot` (e.g. `platformer.autotile.ground`), laid
 * out row-major so artists can author it once and every edge/corner
 * picks itself:
 *
 *        col 0        col 1        col 2        col 3
 *   row 0  isolated     N only       E only       N+E
 *   row 1  S only       N+S          E+S          N+E+S
 *   row 2  W only       N+W          E+W          N+E+W
 *   row 3  S+W          N+S+W        E+S+W        all four
 *
 * (= index `mask`, col = mask % 4, row = floor(mask / 4).)
 *
 * The variant is DERIVED at render time — stage JSON only ever stores
 * the tile type, so undo/redo, copy/paste, fills and drag-moves never
 * have to re-resolve anything.
 */

export const AUTOTILE_GRID = Object.freeze({ cols: 4, rows: 4 });

/**
 * Compute the 4-bit neighbour mask for cell (c, r).
 * @param {(c:number, r:number) => boolean} hasSame true when the probed
 *        cell holds a tile of the same autotile group.
 */
export function autotileMask(hasSame, c, r) {
    let m = 0;
    if (hasSame(c, r - 1)) m |= 1;  // N
    if (hasSame(c + 1, r)) m |= 2;  // E
    if (hasSame(c, r + 1)) m |= 4;  // S
    if (hasSame(c - 1, r)) m |= 8;  // W
    return m;
}

/** Map a mask (0–15) onto its cell in the 4×4 template image. */
export function autotileRegion(mask) {
    const safe = Math.max(0, Math.min(15, mask | 0));
    return {
        gridCols: AUTOTILE_GRID.cols,
        gridRows: AUTOTILE_GRID.rows,
        frameCol: safe % AUTOTILE_GRID.cols,
        frameRow: Math.floor(safe / AUTOTILE_GRID.cols),
    };
}

/**
 * Build a "c,r" → group lookup for every autotile-enabled tile on a
 * layer. One pass per layer per (re)paint keeps neighbour probes O(1).
 * @param {{tiles?: Array}} layer
 * @param {Record<string, {autotile?: {group: string}}>} defs
 * @returns {Map<string, string>}
 */
export function buildAutotileLookup(layer, defs) {
    const map = new Map();
    for (const t of (layer.tiles || [])) {
        const group = defs[t.type]?.autotile?.group;
        if (group) map.set(`${t.c},${t.r}`, group);
    }
    return map;
}
